import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { onAuthStateChanged } from 'firebase/auth';
import { getFactionStoredWrestlers } from '../utils/factionWrestlers';
import { calculateFactionTotalScore } from '../utils/factionScoring';
import { calculateGmMatchPointsByFaction, normalizeGmMatch } from '../utils/gmMatches';
import SectionAd from '../components/ads/SectionAd';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import { resolveSelectedRankingRoster } from '../utils/rankingSelection';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';

// Componenti riutilizzabili
const FactionAvatar = ({ faction, size = 'md', showBorder = false }) => {
  const sizes = {
    sm: { width: '50px', height: '50px' },
    md: { width: '80px', height: '80px' },
  };

  const style = sizes[size] || sizes.md;

  if (faction.imageUrl) {
    return (
      <img
        src={faction.imageUrl}
        alt={faction.name}
        className="rounded-circle"
        style={{
          ...style,
          objectFit: 'cover',
          border: showBorder ? '3px solid var(--brand-secondary)' : 'none',
          boxShadow: showBorder ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center bg-brand-primary"
      style={{
        ...style,
        border: showBorder ? '3px solid var(--brand-secondary)' : 'none',
        boxShadow: showBorder ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'
      }}
    >
      <i className="bi bi-people-fill text-brand-secondary" style={{ fontSize: size === 'md' ? '2rem' : '1rem' }}></i>
    </div>
  );
};

const PodiumCard = ({ factions, rank, userFactionId }) => {
  if (!factions.length) return null;

  // Determiniamo se la fazione dell'utente è presente in questo podio
  const isTiedPosition = factions.length > 1;
  const hasUserFaction = factions.some(f => f.id === userFactionId);

  const cardContent = (
    <>
      {!isTiedPosition && factions[0]?.imageUrl && (
        <div
          className="podium-bg-image"
          style={{ backgroundImage: `url(${factions[0].imageUrl})` }}
        />
      )}

      <div className="podium-bar" />
      <div className="podium-rank">{rank}°</div>

      {factions.map(faction => {
        const isUserFaction = faction.id === userFactionId;
        const factionContent = (
          <>
            {isTiedPosition && faction.imageUrl && (
              <div
                className="podium-entry-bg-image"
                style={{ backgroundImage: `url(${faction.imageUrl})` }}
              />
            )}
            <div className="d-flex flex-column align-items-center position-relative">
              <div className="mb-1" style={{ zIndex: 1 }}>
                <FactionAvatar faction={faction} size="md" showBorder />
              </div>
              <div className="text-center position-relative" style={{ zIndex: 1 }}>
                <div className="podium-name">{faction.name}</div>
                <div className="podium-owner text-muted-brand">{faction.ownerName}</div>
              </div>
            </div>
            {isTiedPosition && isUserFaction && (
              <div className="faction-badge-user">
                <i className="bi bi-person-fill"></i>
              </div>
            )}
          </>
        );

        if (isTiedPosition) {
          return (
            <Link
              key={`${faction.id}-${faction.totalScore}`}
              to={`/factions/${faction.id}`}
              className={`podium-entry podium-entry--linked ${isUserFaction ? 'user-faction' : ''}`}
              id={isUserFaction ? `user-faction-${faction.id}` : undefined}
            >
              {factionContent}
            </Link>
          );
        }

        return (
          <div
            key={`${faction.id}-${faction.totalScore}`}
            className={`position-relative ${isUserFaction ? 'user-faction' : ''}`}
            id={isUserFaction ? `user-faction-${faction.id}` : undefined}
          >
            {factionContent}
          </div>
        );
      })}

      <div className="podium-score text-brand-secondary">
        <AnimatedNumber value={factions[0]?.totalScore || 0} />
      </div>
      {!isTiedPosition && hasUserFaction && (
        <div className="faction-badge-user">
          <i className="bi bi-person-fill"></i>
        </div>
      )}
    </>
  );

  if (isTiedPosition) {
    return (
      <div className={`podium-card podium-card--tied rank-${rank}`}>
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      to={`/factions/${factions[0].id}`}
      className={`podium-card rank-${rank} text-decoration-none d-block`}
      id={hasUserFaction ? `user-faction-${userFactionId}` : undefined}
      style={{ cursor: 'pointer' }}
    >
      {cardContent}
    </Link>
  );
};

const FactionListItem = ({ faction, rank, userFactionId }) => {
  const isUserFaction = faction.id === userFactionId;

  return (
    <Link
      to={`/factions/${faction.id}`}
      className={`list-card ${isUserFaction ? 'user-faction' : ''}`}
      id={isUserFaction ? `user-faction-${faction.id}` : undefined}
    >
      <div className="list-rank">{rank}°</div>
      <div className="list-info">
        <div className="d-flex align-items-center">
          <div className="me-3">
            <FactionAvatar faction={faction} size="sm" />
          </div>
          <div className="flex-grow-1">
            <div className="list-name text-break">{faction.name}</div>
            <div className="list-owner text-truncate">{faction.ownerName}</div>
          </div>
        </div>
      </div>
      <div className="list-score text-brand-secondary">
        <AnimatedNumber value={faction.totalScore || 0} />
      </div>
      {isUserFaction && (
        <div className="faction-badge-user">
          <i className="bi bi-person-fill"></i>
        </div>
      )}
    </Link>
  );
};

const RankingView = ({ factions, title, icon, userFactionId }) => {
  const ranking = calculateRanking(factions);

  return (
    <>
      <div className="podium-container mb-3">
        {ranking.podiumGroups.map((group, idx) => (
          <PodiumCard
            key={idx + 1}
            factions={group}
            rank={idx + 1}
            userFactionId={userFactionId}
          />
        ))}
      </div>
      <div className="list-container">
        {ranking.others.length ? (
          ranking.others.map(faction => (
            <FactionListItem
              key={`${faction.id}-${faction.totalScore}`}
              faction={faction}
              rank={faction.displayRank}
              userFactionId={userFactionId}
            />
          ))
        ) : (
          <div className="list-empty">
            <i className="bi bi-emoji-frown fs-1 text-muted-brand"></i>
            <p className="text-muted-brand">Competizione non iniziata</p>
          </div>
        )}
      </div>
    </>
  );
};

const GroupedRankingView = ({ groupedFactions, userFactionId, searchTerm }) => {
  // Filtra ogni gruppo di roster
  const filteredGroups = {};

  // Funzione per filtrare le fazioni in base alla ricerca
  const filterFactions = (factionsList, term) => {
    if (!term.trim()) return factionsList;
    const lowerTerm = term.toLowerCase();
    return factionsList.filter(f =>
      f.name.toLowerCase().includes(lowerTerm) ||
      f.ownerName.toLowerCase().includes(lowerTerm)
    );
  };

  Object.entries(groupedFactions).forEach(([rosterName, rosterFactions]) => {
    const filtered = filterFactions(rosterFactions, searchTerm);
    if (filtered.length > 0) {
      filteredGroups[rosterName] = filtered;
    }
  });

  if (Object.keys(filteredGroups).length === 0) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-info brand-stat">
          <i className="bi bi-info-circle me-2"></i>
          Nessuna fazione trovata con i criteri di ricerca.
        </div>
      </div>
    );
  }

  return (
    <>
      {Object.entries(filteredGroups).map(([rosterName, rosterFactions]) => {
        const ranking = calculateRanking(rosterFactions);

        return (
          <div key={rosterName} className="roster-group">
            <h3 className="roster-title brand-title">
              {rosterName === 'Indipendente' ? 'Indipendente' :
                rosterName === 'Competitive' ? 'Competitive' : rosterName}
            </h3>
            <div className="podium-container">
              {ranking.podiumGroups.map((group, idx) => (
                <PodiumCard
                  key={idx + 1}
                  factions={group}
                  rank={idx + 1}
                  userFactionId={userFactionId}
                />
              ))}
            </div>
            <div className="list-container">
              {ranking.others.map((faction, index) => (
                <FactionListItem
                  key={faction.id}
                  faction={faction}
                  rank={index + 4}
                  userFactionId={userFactionId}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

// Funzione helper per calcolare il ranking
const calculateRanking = (factionsList) => {
  const withRank = factionsList.reduce((acc, curr, idx, arr) => {
    const prev = acc[idx - 1];
    const rank = prev && prev.totalScore === curr.totalScore
      ? prev.displayRank
      : idx + 1;
    return [...acc, { ...curr, displayRank: rank }];
  }, []);

  const podiumGroups = [1, 2, 3].map(rank =>
    withRank.filter(f => f.displayRank === rank)
  );

  const others = withRank.filter(f => f.displayRank > 3);

  return { podiumGroups, others };
};

const groupFactionsByRoster = (factionsList) => {
  const rosters = {};

  factionsList.forEach(faction => {
    const roster = faction.roster || 'Indy';
    if (!rosters[roster]) rosters[roster] = [];
    rosters[roster].push(faction);
  });

  Object.keys(rosters).forEach(roster => {
    rosters[roster].sort((a, b) => b.totalScore - a.totalScore);
  });

  return rosters;
};

const getGmMatchScoreDate = (match) =>
  match.weekStart || match.kickoffAt || match.closedAt || match.createdAt || null;

const toRankingDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getRankingScoreStartDate = (faction, roster) =>
  toRankingDate(roster?.dataInizioPunteggio || faction?.dataInizioPunteggio);

const hasRankingScoringStarted = (faction, roster, referenceDate = new Date()) => {
  const startDate = getRankingScoreStartDate(faction, roster);
  if (!startDate) return true;
  return referenceDate.getTime() >= startDate.getTime();
};

const withRankingScoreStartDate = (faction, roster) => {
  const startDate = getRankingScoreStartDate(faction, roster);
  return startDate ? { ...faction, dataInizioPunteggio: startDate } : faction;
};

// Componente principale
export default function Ranking() {
  const [wrestlerScores, setWrestlerScores] = useState({});
  const [rawFactions, setRawFactions] = useState([]);
  const [factions, setFactions] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [rostersMap, setRostersMap] = useState({});
  const [gmScoresByRoster, setGmScoresByRoster] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userFactionRoster, setUserFactionRoster] = useState(null);
  const [favoriteFactionId, setFavoriteFactionId] = useState(null);
  const [userFactionIds, setUserFactionIds] = useState([]);
  const [activeTab, setActiveTab] = useState('roster');
  const [groupByRoster, setGroupByRoster] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFactionId, setUserFactionId] = useState(null);

  // Timeout per caricamenti troppo lunghi
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setError("Il caricamento sta impiegando più tempo del previsto");
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading]);

  // 1) Listen to users and check logged in user's faction
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      const unsubUsers = onSnapshot(
        collection(db, 'users'),
        async (snap) => {
          const usersMap = {};
          let nextFavoriteFactionId = null;
          let userFactionId = null;
          let collectedUserFactionIds = [];

          if (user) {
            const userDoc = snap.docs.find(d => d.id === user.uid);
            if (userDoc?.exists()) {
              const userData = userDoc.data();
              collectedUserFactionIds = [
                ...(Array.isArray(userData.factionIds) ? userData.factionIds : []),
                userData.factionId,
                userData.favoriteFactionId
              ]
                .filter(Boolean)
                .filter((id, index, array) => array.indexOf(id) === index);
              nextFavoriteFactionId = userData.favoriteFactionId || null;
              userFactionId =
                userData.favoriteFactionId ||
                userData.factionId ||
                userData.factionIds?.[0] ||
                null;
            }
          }

          snap.docs.forEach(d => {
            usersMap[d.id] = d.data().username || '—';
          });

          setUsersMap(usersMap);
          setFavoriteFactionId(user ? nextFavoriteFactionId : null);
          setUserFactionRoster(prev => (user ? prev : null));
          setUserFactionId(userFactionId);
          setUserFactionIds(collectedUserFactionIds);
        },
        err => {
          console.error("Errore nel caricamento utenti:", err);
          setError("Errore nel caricamento dei dati utente");
          setLoading(false);
        }
      );

      return () => unsubUsers();
    });

    return () => unsubAuth();
  }, []);

  // 2) Listen to wrestler scores
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'wrestlers'),
      snap => {
        const scores = {};
        snap.docs.forEach(d => { scores[d.id] = d.data().score || 0; });
        setWrestlerScores(scores);
      },
      err => {
        console.error("Errore nel caricamento wrestler:", err);
        setError("Errore nel caricamento dei punteggi wrestler");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 3) Listen to raw factions data
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'factions'),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRawFactions(list);
        setLoading(false);
      },
      err => {
        console.error("Errore nel caricamento fazioni:", err);
        setError("Errore nel caricamento delle fazioni");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'rosters'),
      snap => {
        const map = {};
        snap.docs.forEach(d => {
          map[d.id] = { id: d.id, ...d.data() };
        });
        setRostersMap(map);
      },
      err => {
        console.error("Errore nel caricamento roster:", err);
        setError("Errore nel caricamento dei roster");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const gmRosters = Object.values(rostersMap).filter(roster => roster.isGM);
    const activeRosterIds = new Set(gmRosters.map(roster => roster.id));

    setGmScoresByRoster(prev =>
      Object.keys(prev).reduce((next, rosterId) => {
        if (activeRosterIds.has(rosterId)) {
          next[rosterId] = prev[rosterId];
        }
        return next;
      }, {})
    );

    const unsubs = gmRosters.map(roster =>
      onSnapshot(
        collection(db, 'rosters', roster.id, 'matches'),
        async snap => {
          const normalizedMatches = snap.docs.map(docSnap => normalizeGmMatch(docSnap));
          const { matches } = await autoCloseExpiredGmMatches({
            db,
            rosterId: roster.id,
            matches: normalizedMatches,
            roster,
            persist: false
          });
          const startDate = getRankingScoreStartDate(null, roster);
          const scoreableMatches = startDate
            ? matches.filter(match => {
                const matchDate = getGmMatchScoreDate(match);
                return matchDate && matchDate >= startDate;
              })
            : matches;
          setGmScoresByRoster(prev => ({
            ...prev,
            [roster.id]: hasRankingScoringStarted(null, roster)
              ? calculateGmMatchPointsByFaction(scoreableMatches)
              : {}
          }));
        },
        err => {
          console.error("Errore nel caricamento match GM:", err);
        }
      )
    );

    return () => unsubs.forEach(unsub => unsub());
  }, [rostersMap]);

  // 4) Compute derived factions
  useEffect(() => {
    try {
      const computed = rawFactions.map(data => {
        const factionWrestlers = getFactionStoredWrestlers(data);
        const rosterMeta = data.rosterId
          ? rostersMap[data.rosterId]
          : Object.values(rostersMap).find(roster => roster.name === data.roster);
        const isGmRoster = Boolean(rosterMeta?.isGM);
        const factionForScore = withRankingScoreStartDate(data, rosterMeta);
        const totalScore = isGmRoster
          ? (hasRankingScoringStarted(data, rosterMeta)
              ? (gmScoresByRoster[rosterMeta.id]?.[data.id] || 0)
              : 0)
          : calculateFactionTotalScore({
            faction: factionForScore,
            roster: rosterMeta,
            members: factionWrestlers,
            wrestlerScoresById: wrestlerScores,
          });
        const roster = rosterMeta?.name || data.roster || 'Indy';
        const ownerName = usersMap[data.ownerUid] || 'Sconosciuto';

        return {
          id: data.id,
          name: data.name || 'Senza nome',
          imageUrl: data.imageUrl || '',
          ownerName,
          roster,
          rosterId: rosterMeta?.id || data.rosterId || null,
          isGM: isGmRoster,
          totalScore
        };
      });

      setFactions(computed.sort((a, b) => b.totalScore - a.totalScore));
    } catch (err) {
      console.error("Errore nel calcolo delle fazioni:", err);
      setError("Errore nel processare i dati delle fazioni");
    }
  }, [gmScoresByRoster, rawFactions, rostersMap, wrestlerScores, usersMap]);

  // Funzione per filtrare le fazioni in base alla ricerca
  const filterFactions = (factionsList, term) => {
    if (!term.trim()) return factionsList;
    const lowerTerm = term.toLowerCase();
    return factionsList.filter(f =>
      f.name.toLowerCase().includes(lowerTerm) ||
      f.ownerName.toLowerCase().includes(lowerTerm)
    );
  };

  // Funzione per scorrere alla fazione dell'utente
  const scrollToUserFaction = (factionId, type) => {
    const element = document.getElementById(`${type}-${factionId}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Aggiungi un highlight temporaneo
      element.classList.add('highlight-temporary');
      setTimeout(() => {
        element.classList.remove('highlight-temporary');
      }, 2000);
    } else {
      // Se non trovato, cerca in altre tab
      if (type === 'user-faction' && selectedRosterTab) {
        setActiveTab('roster');
        setTimeout(() => {
          const elem = document.getElementById(`${type}-${factionId}`);
          if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  };

  // Filtri per le diverse classifiche
  const userRosterTabs = userFactionIds.reduce((tabs, factionId) => {
    const faction = factions.find(f => f.id === factionId);
    const rosterName = faction?.roster || null;

    if (rosterName && !tabs.includes(rosterName)) {
      tabs.push(rosterName);
    }

    return tabs;
  }, []);
  const favoriteFactionRoster = favoriteFactionId
    ? factions.find(f => f.id === favoriteFactionId)?.roster || null
    : null;
  const selectedRosterTab = resolveSelectedRankingRoster({
    availableRosters: userRosterTabs,
    selectedRoster: userFactionRoster,
    favoriteRoster: favoriteFactionRoster,
  });
  const shouldShowOnboardingCard = !currentUser || !userFactionId;

  useEffect(() => {
    setActiveTab(selectedRosterTab ? 'roster' : 'global');
  }, [selectedRosterTab]);

  const rosterFactions = selectedRosterTab ?
    factions.filter(f => f.roster === selectedRosterTab) : [];
  const userFactionInSelectedRoster = selectedRosterTab
    ? rosterFactions.find(f => f.id === userFactionId) ||
      rosterFactions.find(f => userFactionIds.includes(f.id)) ||
      null
    : null;
  const activeRosterUserFactionId = userFactionInSelectedRoster?.id || userFactionId;
  const shouldShowUserFactionQuickJump = Boolean(
    currentUser &&
    activeRosterUserFactionId &&
    activeTab === 'roster' &&
    userFactionInSelectedRoster
  );


  const globalFactions = factions;
  const groupedGlobalFactions = groupByRoster ? groupFactionsByRoster(globalFactions) : null;

  // Fazioni filtrate per ricerca
  const filteredRosterFactions = filterFactions(rosterFactions, searchTerm);
  const filteredGlobalFactions = filterFactions(globalFactions, searchTerm);

  if (loading) {
    return <LoadingSpinner fullPage={true} />;
  }

  if (error) {
    return (
      <div className="ranking-page">
        <div className="error-container text-center py-5">
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
          <button
            className="btn btn-brand"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Ricarica la pagina
          </button>
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.email === 'luca.biddeci@gmail.com' ||
    currentUser?.email === 'mbho.alc@gmail.com';
  const handleAdminGroupingToggle = () => {
    setGroupByRoster(prev => !prev);
    setActiveTab('global');
  };

  return (
    <div className="ranking-page">
      {/* Header */}
      <div className="ranking-header justify-content-center mb-4">
        <h1 className="ranking-title" style={{
          fontFamily: "'Cinzel Decorative'",
          textShadow: '1px 1px 2px rgba(0, 0, 0)'
        }}>
          <i className="bi bi-trophy-fill text-brand-secondary me-2"></i>
          CLaSSifiCa
        </h1>
        {isAdmin && (
          <button
            type="button"
            className={`ranking-admin-toggle ${groupByRoster ? 'active' : ''}`}
            onClick={handleAdminGroupingToggle}
            aria-pressed={groupByRoster}
            title={groupByRoster ? 'Vista raggruppata attiva' : 'Attiva vista raggruppata'}
          >
            <i className={`bi ${groupByRoster ? 'bi-gear-fill' : 'bi-gear'}`}></i>
          </button>
        )}
      </div>

      {shouldShowUserFactionQuickJump && (
        <div className="quick-jump-buttons mb-1">
          <div className="d-flex justify-content-center">
            <div>
              <button
                className="btn quick-jump-btn main-faction w-100"
                onClick={() => scrollToUserFaction(activeRosterUserFactionId, 'user-faction')}
              >
                <i className="bi bi-person-fill me-1"></i>
                Vai alla tua Fazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs per utenti loggati con fazione */}
      {userRosterTabs.length > 0 && (
        <div className="ranking-tabs mb-2 d-flex align-items-center justify-content-center" style={{ borderBottom: 'none' }}>
          <div className="ranking-tabs__scroller">
            {userRosterTabs.map(rosterName => (
              <button
                key={rosterName}
                className={`tab-link ${selectedRosterTab === rosterName ? 'active' : ''}`}
                onClick={() => {
                  if (isAdmin && groupByRoster) {
                    setGroupByRoster(false);
                  }
                  setUserFactionRoster(rosterName);
                  setActiveTab('roster');
                }}
              >
                <i className='bi bi-diagram-2-fill me-1'></i>
                {rosterName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contenuto principale */}
      {shouldShowOnboardingCard ? (
        <>
          <div className="text-center fs-6 mb-4">
            <div className="card bg-brand-primary">
              <div className="card-body">
                <h5 className="mb-1 brand-title">Inizia a gareggiare!</h5>
                <p className="mb-3 text-light">
                  Crea la tua fazione per partecipare alla classifica e competere con altri giocatori.
                </p>
                {currentUser ? (
                  <Link to="/profile" className="btn btn-brand-secondary fw-bold">
                    Crea la tua prima fazione
                  </Link>
                ) : (
                  <Link to="/auth" className="btn btn-brand-secondary fw-bold">
                    Registrati o Accedi
                  </Link>
                )}
              </div>
            </div>
          </div>
          {groupByRoster ? (
            <GroupedRankingView
              groupedFactions={groupedGlobalFactions}
              userFactionId={userFactionId}
              searchTerm={searchTerm}
            />
          ) : (
            <RankingView
              factions={filteredGlobalFactions}
              icon="bi-globe"
              userFactionId={userFactionId}
            />
          )}
        </>
      ) : (
        <>
          {activeTab === 'roster' ? (
            <RankingView
              factions={filteredRosterFactions}
              icon="bi-diagram-2-fill"
              userFactionId={activeRosterUserFactionId}
            />
          ) : (
            <>
              {groupByRoster ? (
                <GroupedRankingView
                  groupedFactions={groupedGlobalFactions}
                  userFactionId={userFactionId}
                  searchTerm={searchTerm}
                />
              ) : (
                <RankingView
                  factions={filteredGlobalFactions}
                  icon="bi-globe"
                  userFactionId={userFactionId}
                />
              )}
            </>
          )}
          <SectionAd placement="home" className="d-flex justify-content-center my-2" />
        </>
      )}

    </div>
  );
}
